import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Play,
  Pause,
  Square,
  ChevronRight,
  Check,
  Sparkles,
  Coffee,
  Music,
  Palette,
  PenTool,
  Film,
  X
} from 'lucide-react';
import { Button, Card, Modal, Textarea, MoodSelector, type Mood } from '../../components/ui';
import { AppLayout } from '../../components/AppLayout';
import { useAuth } from '../../context';
import { flowSessionsApi, projectsApi, FlowSessionDto, ProjectDto } from '../../services';
import './FlowTimer.css';

// Pre-session ritual items
const defaultRitualItems = [
  { id: '1', text: 'Clear your workspace', icon: '🧹' },
  { id: '2', text: 'Close distracting tabs', icon: '🚫' },
  { id: '3', text: 'Have water nearby', icon: '💧' },
  { id: '4', text: 'Take 3 deep breaths', icon: '🌬️' },
];

// Project type icons
const ProjectTypeIcon: React.FC<{ type: string; size?: number }> = ({ type, size = 24 }) => {
  switch (type) {
    case 'Music':
      return <Music size={size} />;
    case 'Art':
      return <Palette size={size} />;
    case 'Writing':
      return <PenTool size={size} />;
    case 'Video':
      return <Film size={size} />;
    default:
      return <Sparkles size={size} />;
  }
};

// Format seconds to HH:MM:SS
const formatTime = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Encouraging messages
const sessionMessages = [
  "You're in the flow. Keep going!",
  "Every second counts. You're creating.",
  "This is your time to create.",
  "Let the creativity flow through you.",
  "You're showing up for your art.",
];

const completionMessages = [
  "Beautiful session! You showed up.",
  "That was wonderful. Well done!",
  "You made progress today. Celebrate that!",
  "Another step on your creative journey.",
];

type TimerState = 'idle' | 'ritual' | 'running' | 'paused' | 'completed';

export const FlowTimer: React.FC = () => {
  useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('project');

  // Timer state
  const [timerState, setTimerState] = useState<TimerState>('idle');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [sessionMessage, setSessionMessage] = useState('');

  // Session data
  const [activeSession, setActiveSession] = useState<FlowSessionDto | null>(null);
  const [selectedProject, setSelectedProject] = useState<ProjectDto | null>(null);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);

  // Ritual state
  const [ritualItems, setRitualItems] = useState(defaultRitualItems.map(item => ({ ...item, completed: false })));
  const allRitualItemsCompleted = ritualItems.every(item => item.completed);

  // Post-session state
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [journalText, setJournalText] = useState('');
  const [sessionMood, setSessionMood] = useState<Mood | undefined>();
  const [completionMessage, setCompletionMessage] = useState('');

  // Focus mode
  const [isFocusMode, setIsFocusMode] = useState(false);

  // Timer interval ref
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // Fetch projects
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const projectList = await projectsApi.getProjects();
        setProjects(projectList);

        // If project ID is in URL, select it
        if (projectIdFromUrl) {
          const project = projectList.find(p => p.id === projectIdFromUrl);
          if (project) {
            setSelectedProject(project);
          }
        }
      } catch (err) {
        console.error('Error fetching projects:', err);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, [projectIdFromUrl]);

  // Check for active session on mount
  useEffect(() => {
    const checkActiveSession = async () => {
      try {
        const session = await flowSessionsApi.getActiveSession();
        if (session) {
          setActiveSession(session);
          // Calculate elapsed time from session start
          const startTime = new Date(session.startTime).getTime();
          const now = Date.now();
          const elapsed = Math.floor((now - startTime) / 1000);
          setElapsedSeconds(elapsed);
          startTimeRef.current = startTime;
          setTimerState('running');

          // Find the project
          const project = projects.find(p => p.id === session.projectId);
          if (project) {
            setSelectedProject(project);
          }
        }
      } catch (err) {
        console.error('Error checking active session:', err);
      }
    };

    if (!isLoadingProjects) {
      checkActiveSession();
    }
  }, [isLoadingProjects, projects]);

  // Timer logic
  useEffect(() => {
    if (timerState === 'running') {
      timerRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const now = Date.now();
          const elapsed = Math.floor((now - startTimeRef.current) / 1000);
          setElapsedSeconds(elapsed);
        } else {
          setElapsedSeconds(prev => prev + 1);
        }
      }, 1000);

      // Set a random encouraging message
      setSessionMessage(sessionMessages[Math.floor(Math.random() * sessionMessages.length)]);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timerState]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Space to pause/resume (only when in timer states)
      if (e.code === 'Space' && (timerState === 'running' || timerState === 'paused')) {
        e.preventDefault();
        if (timerState === 'running') {
          handlePause();
        } else {
          handleResume();
        }
      }

      // Escape to exit focus mode
      if (e.code === 'Escape' && isFocusMode) {
        setIsFocusMode(false);
      }

      // F to toggle focus mode
      if (e.code === 'KeyF' && !e.metaKey && !e.ctrlKey && (timerState === 'running' || timerState === 'paused')) {
        setIsFocusMode(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerState, isFocusMode]);

  const handleStartRitual = () => {
    if (!selectedProject) {
      // Could show a toast here
      return;
    }
    setTimerState('ritual');
  };

  const handleSkipRitual = async () => {
    await startSession();
  };

  const handleCompleteRitual = async () => {
    await startSession(true);
  };

  const startSession = async (ritualCompleted = false) => {
    if (!selectedProject) return;

    try {
      const session = await flowSessionsApi.startSession({
        projectId: selectedProject.id,
        preRitualCompleted: ritualCompleted,
      });
      setActiveSession(session);
      startTimeRef.current = Date.now();
      setElapsedSeconds(0);
      setTimerState('running');
    } catch (err) {
      console.error('Error starting session:', err);
    }
  };

  const handlePause = () => {
    setTimerState('paused');
  };

  const handleResume = () => {
    // Adjust start time to account for pause duration
    if (startTimeRef.current) {
      const pauseDuration = Date.now() - (startTimeRef.current + elapsedSeconds * 1000);
      startTimeRef.current += pauseDuration;
    }
    setTimerState('running');
  };

  const handleStop = () => {
    setTimerState('completed');
    setCompletionMessage(completionMessages[Math.floor(Math.random() * completionMessages.length)]);
    setShowJournalModal(true);
  };

  const handleCompleteSession = async () => {
    if (!activeSession) return;

    try {
      await flowSessionsApi.endSession(activeSession.id, {
        journalText: journalText || undefined,
        moodAfter: sessionMood,
      });

      // Reset everything
      setActiveSession(null);
      setElapsedSeconds(0);
      setTimerState('idle');
      setShowJournalModal(false);
      setJournalText('');
      setSessionMood(undefined);
      setRitualItems(defaultRitualItems.map(item => ({ ...item, completed: false })));
      startTimeRef.current = null;
      setIsFocusMode(false);
    } catch (err) {
      console.error('Error ending session:', err);
    }
  };

  const handleSkipJournal = async () => {
    if (!activeSession) return;

    try {
      await flowSessionsApi.endSession(activeSession.id, {});

      // Reset
      setActiveSession(null);
      setElapsedSeconds(0);
      setTimerState('idle');
      setShowJournalModal(false);
      setJournalText('');
      setSessionMood(undefined);
      setRitualItems(defaultRitualItems.map(item => ({ ...item, completed: false })));
      startTimeRef.current = null;
      setIsFocusMode(false);
    } catch (err) {
      console.error('Error ending session:', err);
    }
  };

  const toggleRitualItem = (id: string) => {
    setRitualItems(items =>
      items.map(item =>
        item.id === id ? { ...item, completed: !item.completed } : item
      )
    );
  };

  // Focus mode view
  if (isFocusMode) {
    return (
      <div className="flow-timer flow-timer--focus">
        <button
          className="flow-timer__exit-focus"
          onClick={() => setIsFocusMode(false)}
          aria-label="Exit focus mode"
        >
          <X size={24} />
        </button>

        <div className="flow-timer__focus-content">
          <div className="flow-timer__focus-project">
            {selectedProject && (
              <>
                <ProjectTypeIcon type={selectedProject.projectType} size={32} />
                <span>{selectedProject.title}</span>
              </>
            )}
          </div>

          <div className="flow-timer__focus-display">
            <span className="flow-timer__focus-time">{formatTime(elapsedSeconds)}</span>
          </div>

          <p className="flow-timer__focus-message">{sessionMessage}</p>

          <div className="flow-timer__focus-controls">
            {timerState === 'running' ? (
              <button
                className="flow-timer__focus-button flow-timer__focus-button--pause"
                onClick={handlePause}
              >
                <Pause size={32} />
              </button>
            ) : (
              <button
                className="flow-timer__focus-button flow-timer__focus-button--play"
                onClick={handleResume}
              >
                <Play size={32} />
              </button>
            )}
            <button
              className="flow-timer__focus-button flow-timer__focus-button--stop"
              onClick={handleStop}
            >
              <Square size={32} />
            </button>
          </div>

          <p className="flow-timer__focus-hint">
            Press <kbd>Space</kbd> to pause/resume · <kbd>F</kbd> or <kbd>Esc</kbd> to exit focus mode
          </p>
        </div>
      </div>
    );
  }

  return (
    <AppLayout pageTitle="Flow Session">
    <div className="flow-timer">
      {/* Ambient background */}
      <div className="flow-timer__ambient">
        <div className="flow-timer__ambient-shape flow-timer__ambient-shape--1" />
        <div className="flow-timer__ambient-shape flow-timer__ambient-shape--2" />
      </div>

      {/* Main Content */}
      <main className="flow-timer__main">
        {/* Idle State - Project Selection */}
        {timerState === 'idle' && (
          <div className="flow-timer__idle">
            <div className="flow-timer__greeting">
              <Coffee size={32} className="flow-timer__greeting-icon" />
              <h2>Ready to create?</h2>
              <p>Choose a work to focus on, then prepare your space.</p>
            </div>

            <Card variant="elevated" padding="lg" className="flow-timer__project-selector">
              <h3 className="flow-timer__section-title">Select a Work</h3>

              {isLoadingProjects ? (
                <div className="flow-timer__loading">
                  <div className="flow-timer__spinner" />
                  <p>Loading your works...</p>
                </div>
              ) : projects.length > 0 ? (
                <div className="flow-timer__project-grid">
                  {projects.map(project => (
                    <button
                      key={project.id}
                      className={`flow-timer__project-item ${selectedProject?.id === project.id ? 'flow-timer__project-item--selected' : ''}`}
                      onClick={() => setSelectedProject(project)}
                    >
                      <ProjectTypeIcon type={project.projectType} size={24} />
                      <span className="flow-timer__project-name">{project.title}</span>
                      {selectedProject?.id === project.id && (
                        <Check size={18} className="flow-timer__project-check" />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flow-timer__empty">
                  <p>No works yet. Create one first!</p>
                  <Button onClick={() => navigate('/projects')} size="sm">
                    Go to Works
                  </Button>
                </div>
              )}
            </Card>

            <Button
              size="lg"
              className="flow-timer__start-button"
              onClick={handleStartRitual}
              disabled={!selectedProject}
            >
              <Play size={24} />
              Begin Session
            </Button>
          </div>
        )}

        {/* Ritual State */}
        {timerState === 'ritual' && (
          <div className="flow-timer__ritual">
            <div className="flow-timer__ritual-header">
              <span className="flow-timer__ritual-icon">🧘</span>
              <h2>Prepare Your Space</h2>
              <p>A moment to get ready. Take your time.</p>
            </div>

            <Card variant="elevated" padding="lg" className="flow-timer__ritual-card">
              <div className="flow-timer__ritual-items">
                {ritualItems.map(item => (
                  <button
                    key={item.id}
                    className={`flow-timer__ritual-item ${item.completed ? 'flow-timer__ritual-item--completed' : ''}`}
                    onClick={() => toggleRitualItem(item.id)}
                  >
                    <span className="flow-timer__ritual-item-icon">{item.icon}</span>
                    <span className="flow-timer__ritual-item-text">{item.text}</span>
                    <span className="flow-timer__ritual-item-check">
                      {item.completed ? <Check size={20} /> : <span className="flow-timer__ritual-item-circle" />}
                    </span>
                  </button>
                ))}
              </div>

              <div className="flow-timer__ritual-actions">
                <Button
                  variant="ghost"
                  onClick={handleSkipRitual}
                >
                  Skip ritual
                </Button>
                <Button
                  onClick={handleCompleteRitual}
                  disabled={!allRitualItemsCompleted}
                >
                  I'm Ready <ChevronRight size={18} />
                </Button>
              </div>
            </Card>
          </div>
        )}

        {/* Running/Paused State */}
        {(timerState === 'running' || timerState === 'paused') && (
          <div className="flow-timer__active">
            {/* Project info */}
            <div className="flow-timer__active-project">
              {selectedProject && (
                <>
                  <ProjectTypeIcon type={selectedProject.projectType} size={20} />
                  <span>{selectedProject.title}</span>
                </>
              )}
            </div>

            {/* Timer display */}
            <div className={`flow-timer__display ${timerState === 'paused' ? 'flow-timer__display--paused' : ''}`}>
              <span className="flow-timer__time">{formatTime(elapsedSeconds)}</span>
              {timerState === 'paused' && (
                <span className="flow-timer__paused-label">Paused</span>
              )}
            </div>

            {/* Encouraging message */}
            <p className="flow-timer__message">{sessionMessage}</p>

            {/* Controls */}
            <div className="flow-timer__controls">
              {timerState === 'running' ? (
                <Button
                  variant="secondary"
                  size="lg"
                  className="flow-timer__control-button"
                  onClick={handlePause}
                >
                  <Pause size={24} />
                  Pause
                </Button>
              ) : (
                <Button
                  size="lg"
                  className="flow-timer__control-button"
                  onClick={handleResume}
                >
                  <Play size={24} />
                  Resume
                </Button>
              )}
              <Button
                variant="secondary"
                size="lg"
                className="flow-timer__control-button flow-timer__control-button--stop"
                onClick={handleStop}
              >
                <Square size={24} />
                Done
              </Button>
            </div>

            {/* Focus mode button */}
            <button
              className="flow-timer__focus-toggle"
              onClick={() => setIsFocusMode(true)}
            >
              <Sparkles size={18} />
              Enter Focus Mode
            </button>

            {/* Keyboard hints */}
            <p className="flow-timer__hint">
              Press <kbd>Space</kbd> to {timerState === 'running' ? 'pause' : 'resume'} · <kbd>F</kbd> for focus mode
            </p>
          </div>
        )}
      </main>

      {/* Post-Session Journal Modal */}
      <Modal
        isOpen={showJournalModal}
        onClose={() => {}} // Don't allow closing by clicking outside
        closeOnOverlayClick={false}
        closeOnEscape={false}
        showCloseButton={false}
        size="md"
      >
        <div className="flow-timer__journal">
          <div className="flow-timer__journal-header">
            <span className="flow-timer__journal-icon">✨</span>
            <h2>Session Complete!</h2>
            <p className="flow-timer__journal-message">{completionMessage}</p>
            <div className="flow-timer__journal-stats">
              <span className="flow-timer__journal-duration">{formatTime(elapsedSeconds)}</span>
              <span className="flow-timer__journal-project">{selectedProject?.title}</span>
            </div>
          </div>

          <div className="flow-timer__journal-content">
            <div className="flow-timer__journal-section">
              <label className="flow-timer__journal-label">How did it go?</label>
              <MoodSelector
                value={sessionMood}
                onChange={setSessionMood}
                size="md"
              />
            </div>

            <div className="flow-timer__journal-section">
              <label className="flow-timer__journal-label">What did you discover? (Optional)</label>
              <Textarea
                placeholder="Any insights, ideas, or reflections..."
                value={journalText}
                onChange={(e) => setJournalText(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <div className="flow-timer__journal-actions">
            <Button variant="ghost" onClick={handleSkipJournal}>
              Skip
            </Button>
            <Button onClick={handleCompleteSession}>
              Save & Finish
            </Button>
          </div>
        </div>
      </Modal>

    </div>
    </AppLayout>
  );
};

export default FlowTimer;
